"use client";

import { FormEvent, useEffect, useId, useRef, useState } from "react";
import { TimelineDatePicker } from "@/components/TimelineDatePicker";
import { submitToFormHandler } from "@/lib/form-handler";
import { RECAPTCHA_ACTIONS, loadRecaptcha } from "@/lib/recaptcha";
import { projectTypes, REQUEST_AUDIT_FORM_ACTION, requestAuditClientLogos } from "@/lib/request-audit";
import styles from "./RequestAuditModal.module.css";

type FormState = "idle" | "submitting" | "success" | "error";

const logoColumns = [
  requestAuditClientLogos.slice(0, Math.ceil(requestAuditClientLogos.length / 2)),
  requestAuditClientLogos.slice(Math.ceil(requestAuditClientLogos.length / 2)),
];

export function RequestAuditModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formState, setFormState] = useState<FormState>("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const titleId = useId();
  const fieldId = useId();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Opening the modal is the earliest reliable signal that this visitor is going
  // to submit, and reCAPTCHA v3 needs the head start to score them by submit time.
  useEffect(() => {
    if (isOpen) void loadRecaptcha();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFormState("idle");
      formRef.current?.reset();
    }
  }, [isOpen]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormState("submitting");
    const form = event.currentTarget;
    try {
      await submitToFormHandler(REQUEST_AUDIT_FORM_ACTION, new FormData(form), RECAPTCHA_ACTIONS.audit);
      setFormState("success");
      form.reset();
    } catch {
      setFormState("error");
    }
  }

  if (!isOpen) return null;

  const closeButton = (
    <button type="button" className={`${styles.requestModalClose} ${styles.wInlineBlock}`} aria-label="Close" onClick={onClose}>
      <img src="/images/Vector.svg" alt="" />
    </button>
  );

  return (
    <div className={styles.auditModalWrapper} style={{ display: "flex", opacity: 1 }}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.requestModalHeader}>
          <div className={styles.requestModalHeaderTop}>
            <img src="/images/logo-mixed.svg" width={187} alt="Quantstamp logo" className={styles.requestAuditBrand} />
            <h2 id={titleId} className={styles.requestAuditTitle}>Request an Audit</h2>
            <p className={styles.paragraph6}>Join leading Web3 and established enterprises in securing your project.</p>
            <div className={styles.divBlock114}>
              {logoColumns.map((column, index) => (
                <div className={styles.divBlock115} key={index}>
                  {column.map((logo) => (
                    <img src={logo.src} loading="lazy" alt={logo.alt} key={logo.src} className={styles.auditModalLogo} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* No `w-form` here: React owns this form's submit, and Webflow's
            delegated `.w-form form` handler throws on forms it never inited. */}
        <div className={styles.requestAuditPardotForm}>
          {formState === "success" ? (
            <div className={`${styles.successMessage2} ${styles.wFormDone}`} style={{ display: "block" }}>
              {closeButton}
              <div>
                <div className={styles.divBlock27}>
                  <img src="/images/solid-sharp-check-shield-1.svg" alt="" className={styles.image54} />
                  <div className={styles.textBlock24}>We received your request successfully</div>
                </div>
              </div>
              <p className={styles.paragraph4}>
                Thank you for expressing your interest in a smart contract audit. You will receive a confirmation email
                from us and our team will be in touch with you within the next few days.
                <br />
                <br />
                In the meantime, please consider getting your code and documentation ready in accordance with the Audit
                Readiness Checklist.
              </p>
              <a href="/audit-readiness-guide" className={`${styles.linkBlock16} ${styles.modalLink} ${styles.wInlineBlock}`}>
                <img src="/images/Button-5_1.svg" loading="lazy" alt="" className={`${styles.auditReadinessButton} ${styles.modalRequestButton}`} />
              </a>
            </div>
          ) : (
            <>
              <form
                ref={formRef}
                action={REQUEST_AUDIT_FORM_ACTION}
                method="post"
                className={`${styles.form4} ${styles.wClearfix}`}
                onSubmit={submit}
              >
                {closeButton}
                <div className={styles.divBlock35}>
                  <label htmlFor={`${fieldId}-full_name`} className={styles.fieldLabel}>Full Name</label>
                  <input className={`${styles.textField} ${styles.wInput}`} maxLength={256} name="full_name" type="text" id={`${fieldId}-full_name`} autoComplete="name" required />
                </div>
                <div>
                  <label htmlFor={`${fieldId}-telegram`} className={styles.fieldLabel}>Telegram</label>
                  <input className={`${styles.textField} ${styles.emailInputAuditRequest} ${styles.wInput}`} maxLength={256} name="telegram" type="text" id={`${fieldId}-telegram`} autoComplete="off" required />
                </div>
                <div>
                  <label htmlFor={`${fieldId}-email`} className={styles.fieldLabel}>Email</label>
                  <input className={`${styles.textField} ${styles.emailInputAuditRequest} ${styles.wInput}`} maxLength={256} name="email" type="email" id={`${fieldId}-email`} autoComplete="email" required />
                </div>
                <div>
                  <label htmlFor={`${fieldId}-project_name`} className={styles.fieldLabel}>Project Name or Website URL</label>
                  <input className={`${styles.textField} ${styles.wInput}`} maxLength={256} name="project_name" type="text" id={`${fieldId}-project_name`} required />
                </div>
                <div>
                  <label htmlFor={`${fieldId}-project_type`} className={styles.fieldLabel}>Project Type</label>
                  <select id={`${fieldId}-project_type`} name="project_type" className={`${styles.selectField} ${styles.wSelect}`} defaultValue="" required>
                    <option value="" disabled>-- Select --</option>
                    {projectTypes.map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor={`${fieldId}-timeline_deadline`} className={styles.fieldLabel}>Timeline</label>
                  <TimelineDatePicker
                    key={isOpen ? "open" : "closed"}
                    id={`${fieldId}-timeline_deadline`}
                    name="timeline_deadline"
                    inputClassName={`${styles.textField} ${styles.wInput}`}
                    required
                  />
                </div>
                <div>
                  <label htmlFor={`${fieldId}-project_description`} className={styles.fieldLabel}>Briefly describe the purpose of your project</label>
                  <textarea
                    id={`${fieldId}-project_description`}
                    name="project_description"
                    maxLength={5000}
                    placeholder="I’m looking to discuss a potential audit of..."
                    className={`${styles.textField} ${styles.area} ${styles.wInput}`}
                    required
                  />
                </div>
                <div className={`${styles.v5LightCta} ${styles.mb40} ${styles.solutions}`}>
                  <input
                    type="submit"
                    className={`${styles.v5LightCtaLink3} ${styles.form} ${styles.wButton}`}
                    value={formState === "submitting" ? "Submitting..." : "Submit >"}
                    disabled={formState === "submitting"}
                  />
                </div>
              </form>
              {formState === "error" && (
                <div className={`${styles.errorMessage} ${styles.wFormFail}`} style={{ display: "block" }} role="alert">
                  <div className={styles.submitErrorDiv}>
                    <img src="/images/error.svg" loading="lazy" alt="" />
                    <div className={styles.textBlock47}>Oops! Something went wrong while submitting the form.</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className={styles.modalCloseArea} role="presentation" onClick={onClose} />
    </div>
  );
}
